import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody } from "reactstrap";
import Axios from 'axios';
import { Auth } from "aws-amplify";
import { useSelector } from "react-redux";
import Skeleton from 'react-loading-skeleton';
import Head from 'src/layout/head/Head';
import { Modal, ModalBody, ModalHeader, Button, Spinner, ModalFooter, CardHeader, CardTitle } from "reactstrap";

import { PlusCircleIcon, XMarkIcon, CheckCircleIcon, UserIcon, XCircleIcon } from '@heroicons/react/24/outline';

import AmexLogo from '../../integrations/images/amex.png'

import API_ROUTES from "src/config/api";

import 'react-loading-skeleton/dist/skeleton.css'

const PaymentProvider = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState([]);

  const [loading, setLoading] = useState(true);

  const [connections, setConnections] = useState([]);
  const { organisation } = useSelector((state) => state);

  /**
   * Toggle the modal for adding a new AMEX connection
   */
  const toggleModal = () => {
    setErrors({});
    setUsername('');
    setPassword('');
    setModalIsOpen(!modalIsOpen);
  }

  /**
   * Handle Form Submission from the modal
   * @param {*} e 
   * @returns 
   */
  const handleSubmit = async () => {
    setErrors({});
    setIsSubmitting(true);

    let returnedErrors = {};
    if (username.length < 1) {
      returnedErrors.username = 'Username is required';
    }

    if (password.length < 1) {
      returnedErrors.password = 'Password is required';
    }

    if (Object.keys(returnedErrors).length > 0) {
      setErrors(returnedErrors);
      setIsSubmitting(false);
      return;
    }

    const user = await Auth.currentAuthenticatedUser();

    await Axios(
      API_ROUTES.reporting['integration:amex:put'](organisation.organisation),
      {
        method: 'put',
        data: {
          organisation: organisation.organisation,
          username: username,
          password: password,
        },
        headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
      }
    );

    setModalIsOpen(false);
    setIsSubmitting(false);
    getConnections();
  }

  const deleteConnection = async (connectionId) => {
    setLoading(true);
    const user = await Auth.currentAuthenticatedUser();
    await Axios(
      API_ROUTES.reporting['integration:amex:delete'](organisation.organisation, connectionId),
      {
        method: 'delete',
        headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
      }
    );
    getConnections();
  }

  const getConnections = useCallback(async () => {
    setLoading(true);

    if (!organisation?.organisation) {
      return null;
    }

    const user = await Auth.currentAuthenticatedUser();

    const result = await Axios(
      API_ROUTES.reporting['integration:amex:get'](organisation.organisation),
      {
        method: 'get',
        headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
      }
    );

    setConnections(result.data.data);
    setLoading(false);
  }, [organisation.id]);

  useEffect(() => {
    getConnections();
  }, [organisation.organisation]);

  if (loading === true) {
    return (
      <React.Fragment>

        <Head title="AMEX Integration" />

        <div className="mb-4 grid gap-6 md:grid-cols-2">
          <Skeleton count={1} height="200px" className="mb-2" style={{
            width: '100%',
          }} />

          <Skeleton count={1} height="200px" className="mb-2" style={{
            width: '100%',
          }} />

          <Skeleton count={1} height="200px" className="mb-2" style={{
            width: '100%',
          }} />

          <Skeleton count={1} height="200px" className="mb-2" style={{
            width: '100%',
          }} />
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>

      <Head title="AMEX Integration" />


      <div className="mb-4 grid gap-6 md:grid-cols-2">

        {connections.length > 0 && connections.map((connection, index) => (
          <Card key={index} className="relative border-primary/10 border border-slate-300 rounded-lg overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-b from-primary/5 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />

            <CardHeader className="pb-0 bg-slate-50 clear-both">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <CardTitle className="font-normal text-lg text-primary">Amex Connection <strong>{index + 1}</strong></CardTitle>
                </div>
                <div className={`-mr-5 -mt-2 flex items-center gap-1.5  px-2.5 py-1 rounded-full text-xs font-medium`}>
                  <Button
                    size="sm"
                    className='bg-red-500 border-red-500'
                    onClick={() => deleteConnection(connection.id)}
                  >
                    Delete Connection
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardBody className='pb-0 mb-0'>
              <div>
                <div className="grid grid-cols-2 gap-4 text-sm mb-10 clear-both mx-1">
                  <div className="space-y-1">
                    <p className="text-muted-foreground font-bold">Username</p>
                    <div className="flex items-center gap-1.5">
                      <UserIcon className="h-4 w-4" />
                      <span>{connection.username}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-muted-foreground font-bold">Status</p>
                    {!connection.failed_at && (
                      <div className="flex items-center gap-1.5 text-emerald-700">
                        <CheckCircleIcon className="h-4 w-4" />
                        <span>Connected</span>
                      </div>
                    )}
                    {connection.failed_at && (
                      <div className="flex items-center gap-1.5 text-red-500">
                        <XCircleIcon className="h-4 w-4" />
                        <span>Connection Failed</span>
                      </div>
                    )}

                  </div>
                </div>

              </div>
            </CardBody>
          </Card>
        ))}

        <Card
          className="border-dashed border-2 border-muted-foreground/20 bg-muted/50 h-100 mt-0 cursor-pointer"
          onClick={toggleModal}
        >
          <CardBody className="flex flex-col items-center justify-center text-center p-6">
            <PlusCircleIcon className="!h-10 !w-10 block mb-2 text-violet-800" />
            <h3 className="font-semibold text-muted-foreground text-violet-800">Add New Connection</h3>
            <p className="text-sm text-muted-foreground mt-1">Connect your AMEX account</p>
          </CardBody>
        </Card>

      </div>

      <Modal isOpen={modalIsOpen} toggle={toggleModal} >
        <ModalHeader
          toggle={toggleModal}
          className="bg-secondary text-white text-2xl"
          close={
            <button className="close !-mt-[12px]" onClick={toggleModal}>
              <XMarkIcon className="!h-6 !w-6 text-white mt-[2px]" />
            </button>
          }
        >
          <img src={AmexLogo} alt="AMEX Logo" className="h-8 inline -ml-3 -mt-1" />
          New AMEX Connection
        </ModalHeader>
        <ModalBody className="modal-body-xxl text-center px-4">
          <div className="nk-modal">
            <div className="text-muted-foreground">
              <p className="mb-4 text-left text-sm">
                Please enter your AMEX credentials to create a connection.
              </p>
            </div>
            <div className="mb-4">

              <div className='text-left mb-4'>
                <p className="text-muted-foreground mb-1 text-left text-slate-800 font-bold text-xs">Username</p>
                <input type="text" placeholder="Username" className={`py-4 form-control ${errors.username ? 'is-invalid' : ''}`} value={username} onChange={(e) => setUsername(e.target.value)} />
                {errors.username && (
                  <p className="text-danger text-xs">{errors.username}</p>
                )}
                {!errors.username && (
                  <p className="text-muted-foreground text-xs">Your AMEX username for the portal (not email address)</p>
                )}
              </div>

              <div className='text-left'>
                <p className="text-muted-foreground mb-1 text-left text-slate-800 font-bold text-xs">Password</p>
                <input type="password" placeholder="Password" className={`py-4 form-control ${errors.password ? 'is-invalid' : ''}`} value={password} onChange={(e) => setPassword(e.target.value)} />
                {errors.password && (
                  <p className="text-danger text-xs">{errors.password}</p>
                )}
                {!errors.password && (
                  <p className="text-muted-foreground text-xs">Your AMEX password for the portal</p>
                )}
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="bg-light !py-4">
          <Button
            className="bg-[#006bd5] border-[#006bd5] bg:hover-[#0255a6] text-white cursor-pointer"
            type="submit"
            size="lg"
            disabled={isSubmitting === true}
            onClick={() => handleSubmit()}
          >
            {isSubmitting ? <Spinner size="sm" color="light"> </Spinner> : 'Create Connection'}
          </Button>
        </ModalFooter>
      </Modal>

    </React.Fragment>
  )
};

export default PaymentProvider;
